import React, { useState, useEffect, useContext, useMemo } from "react";
import ReusableModal from "./ReusableModal";
import "./Settings.css";
import AuthContext from "../../../../utils/secure-route/AuthContext";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import useSwal from "../../../common/ReusableComponent/useSwal";
import { useToastHandler } from "../../../common/ToastHandler";
import { ToastContainer } from "react-toastify";
import { capitalizeFirstLetter } from "../../../../utils/helper/common";
import ReusableTable from "../../../common/ReusableComponent/reusableTable/ReusableTable";
import PaginatedSearchSelect from "../../../common/ReusableComponent/PaginatedSearchSelect/PaginatedSearchSelect";
import ChipList from "../../../common/ReusableComponent/ChipList/ChipList";
import { Boxes, Download, Eye, FileText, Mail, Plus, Printer, Store, Tags, Users, WalletCards } from "lucide-react";
import "./VendorProfile.css";

function ManageVendor() {
  const { user } = useContext(AuthContext);
  const storeId = user.store;
  const [showModal, setShowModal] = useState(false);
  const [vendorName, setVendorName] = useState("");
  const [vendorEmail, setVendorEmail] = useState("");
  const [vendorType, setVendorType] = useState("");
  const [vendorDepartments, setVendorDepartments] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [openingBalance, setOpeningBalance] = useState("");
  const [emailError, setEmailError] = useState("");
  const [vendors, setVendors] = useState([]);
  const [serviceData, setServiceData] = useState([]);
  const [editVendorId, setEditVendorId] = useState(null);
  const [loading, setLoading] = useState(true);
  const { Post, Put, Patch, GetPaginatedData , Delete} = useApi();
  const { confirmDelete } = useSwal();
  const { handleToast } = useToastHandler();
  const [RefreshTrigger, setRefreshTrigger] = useState(0);
  const [profileVendor, setProfileVendor] = useState(null);
  const [vendorInvoices, setVendorInvoices] = useState([]);
  const [vendorStock, setVendorStock] = useState([]);
  const [profileLoading, setProfileLoading] = useState(false);
  const [ledgerFrom, setLedgerFrom] = useState("");
  const [ledgerTo, setLedgerTo] = useState("");

  const validateEmail = (email) => {
    const emailRegex = /^[\w\.-]+@[a-zA-Z\d\.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setVendorEmail(email);

    if (email && !validateEmail(email)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  const fetchVendorData = async ({
    page = 1,
    searchTerm = "",
    sort = "",
  } = {}) => {
    const trimmedSearchTerm = searchTerm.trim();

    try {
      setLoading(true);

      const response = await GetPaginatedData("vendorDepartmentData", {
        page,
        search: trimmedSearchTerm || undefined,
        queryParams: {
          ...(sort && { sort }),
        },
      });

      const results = response?.data || [];
      const totalPages = Math.ceil((response?.totalCount || 0) / 10);

      return { data: results, totalPages };
    } catch (error) {
      console.error("Error fetching vendor data:", error);
      handleToast({
        type: "ERROR",
        message: "Error fetching vendor data.",
      });
      return { data: [], totalPages: 1 };
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceData = async ({
  page = 1,
  searchTerm = "",
  sort = "",
} = {}) => {

  const trimmedSearchTerm = searchTerm.trim();
  try {
    const response = await GetPaginatedData("vendorDepartmentServiceData", {
      page,
      search: trimmedSearchTerm || undefined,
      queryParams: {
        ...(sort && { sort }),
        store: storeId
      }
    });

    const results = (response?.data || []).map(item => {
      if (item.all_department) {
        return {
          id: item.id,
          ref_id: item.all_department,
          type: "department",
          title: item.all_department_detail?.title
        };
      }
      if (item.all_expense_type) {
        return {
          id: item.id,
          ref_id: item.all_expense_type,
          type: "expense",
          title: item.all_expense_type_detail?.title
        };
      }
      return null;
    }).filter(Boolean);
    const totalPages = Math.ceil((response?.totalCount || 0) / 10);
    return { data: results, totalPages };
  } catch (error) {

    console.error("Error fetching service data:", error);

    handleToast({
      type: "ERROR",
      message: "Error fetching service data."
    });

    return { data: [], totalPages: 1 };

  }
};

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);

      try {
        const [vendorResponse, serviceResponse] = await Promise.all([
          fetchVendorData(),
          fetchServiceData(),
        ]);

        setVendors(vendorResponse.data);
        setServiceData(serviceResponse.data);
      } catch (error) {
        console.error("Error loading initial data:", error);
        handleToast({
          type: "ERROR",
          message: "Error loading data.",
        });
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  const handleShow = () => setShowModal(true);
  const handleClose = () => {
    setShowModal(false);
    setVendorName("");
    setVendorEmail("");
    setVendorType("");
    setVendorDepartments([]);
    setSelectedDepartments([]);
    setOpeningBalance("");
    setEditVendorId(null);
  };
  const handleClearVendor = () => {
    setVendorName("");
    setVendorEmail("");
    setVendorType("");
    setVendorDepartments([]);
    setSelectedDepartments([]);
    setOpeningBalance("");
  };
  const handleEdit = (id) => {
    const vendor = vendors.find((v) => v.id === id);
    if (!vendor) {
      console.error("Could not find vendor for id:", id);
      return;
    }
    setVendorName(vendor.name);
    setVendorEmail(vendor.email || "");
    setVendorType(vendor.type);
    
    const deptData = (vendor.vendor_department_service || []).map(deptId => {
      const dept = serviceData.find(s => s.id === deptId);
      return dept ? { id: dept.id, title: dept.title } : null;
    }).filter(Boolean);
    
    setVendorDepartments(deptData);
    setSelectedDepartments(deptData);
    setOpeningBalance(vendor.opening_balance);
    setEditVendorId(vendor.id);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (vendorEmail && !validateEmail(vendorEmail)) {
      handleToast({
        type: "ERROR",
        message: "Please enter a valid email address",
      });
      return;
    }

    try {
      const isDuplicate = vendors.some(
        (vendor) =>
          vendor.name.toLowerCase() === vendorName.toLowerCase() &&
          vendor.id !== editVendorId
      );

      if (isDuplicate) {
        handleToast({
          type: "ERROR",
          message: "Vendor with the same name already exists!.",
        });
        return;
      }

      const payload = {
        name: vendorName,
        email: vendorEmail,
        type: vendorType,
        opening_balance: openingBalance,
        vendor_department_service: vendorDepartments.map(d => d.id),
        store: storeId,
        is_deleted: false,
      };

      if (editVendorId) {
        const updatedVendor = await Put(
          "vendorDepartmentData",
          editVendorId,
          payload
        );

        setVendors((prev) =>
          prev.map((vendor) =>
            vendor.id === editVendorId ? updatedVendor.data : vendor
          )
        );

        handleToast({
          type: "SUCCESS",
          message: "Vendor updated successfully!.",
        });
      } else {
        const response = await Post("vendorDepartmentData", payload);
        setVendors((prev) => [...prev, response.data]);

        handleToast({
          type: "SUCCESS",
          message: "Vendor added successfully.",
        });
      }

      setRefreshTrigger((prev) => prev + 1);
      handleClose();
    } catch (error) {
      console.error("Error saving vendor:", error);
      handleToast({
        type: "ERROR",
        message: "Error saving vendor!",
      });
    }
  };

  const filteredVendors = (vendors || []).filter(
    (vendor) => !vendor.is_deleted
  );

  const handleDelete = async (id) => {
    const result = await confirmDelete();
    if (!result.isConfirmed) {
      return;
    }

    try {
      const payload = { is_deleted: true };
      await Delete("vendorDepartmentData", id, payload);
      setVendors((prev) => prev.filter((vendor) => vendor.id !== id));

      handleToast({
        type: "SUCCESS",
        message: "Vendor deleted successfully!",
      });
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Error deleting vendor:", error);
      handleToast({
        type: "ERROR",
        message: "Error deleting vendor!",
      });
    }
  };

  const handleVendorNameChange = (e) => {
    const value = e.target.value;
    setVendorName(capitalizeFirstLetter(value));
  };

  const handleDepartmentSelect = (selected) => {
    if (selected && !vendorDepartments.some(d => d.id === selected.id)) {
      const title = selected.all_department_detail?.title || selected.all_expense_type_detail?.title || "";
      if (!title) {
        console.error("No title found for selected item:", selected);
        handleToast({ type: "ERROR", message: "Unable to add item - no title found" });
        return;
      }
      const newDept = { id: selected.id, title: title };
      setVendorDepartments((prev) => [...prev, newDept]);
      setSelectedDepartments((prev) => [...prev, newDept]);
    }
  };

  const handleChipRemove = (item) => {
    setVendorDepartments((prev) => prev.filter((dept) => dept.id !== item.id));
    setSelectedDepartments((prev) => prev.filter((dept) => dept.id !== item.id));
  };

  const openVendorProfile = async (vendor) => {
    setProfileVendor(vendor);
    setVendorInvoices([]);
    setVendorStock([]);
    setLedgerFrom("");
    setLedgerTo("");
    setProfileLoading(true);
    try {
      const fetchAll = async (api, queryParams) => {
        const rows = [];
        let page = 1;
        let totalCount = 1;
        while (rows.length < totalCount && page <= 100) {
          const response = await GetPaginatedData(api, { page, queryParams: { ...queryParams, page_size: 100 } });
          rows.push(...(response?.data || []));
          totalCount = Number(response?.totalCount || rows.length);
          if (!response?.nextPage) break;
          page += 1;
        }
        return rows;
      };
      const [invoices, stockLots] = await Promise.all([
        fetchAll("invoiceData", { store: storeId, vendor_department: vendor.id, type: vendor.type }),
        fetchAll("productInventoryData", { store: storeId, vendor: vendor.id }),
      ]);
      setVendorInvoices(invoices.filter(invoice => !invoice.is_deleted));
      setVendorStock(stockLots);
    } catch (error) {
      handleToast({ type: "ERROR", message: "Unable to load this vendor's purchases." });
    } finally {
      setProfileLoading(false);
    }
  };

  const ledger = useMemo(() => {
    if (!profileVendor) return [];
    const from = ledgerFrom ? new Date(`${ledgerFrom}T00:00:00`) : null;
    const to = ledgerTo ? new Date(`${ledgerTo}T23:59:59`) : null;
    const hasOpeningInvoice = vendorInvoices.some(invoice => invoice.invoice_no === "opening_balance");
    let balance = 0;
    const rows = [];
    const allInvoices = [...vendorInvoices];
    if (!hasOpeningInvoice && Number(profileVendor.opening_balance || 0)) allInvoices.push({ id: "opening", date: profileVendor.created_at, invoice_no: "opening_balance", status: "opening", amount: profileVendor.opening_balance, after_discount: profileVendor.opening_balance, remaining_amount: profileVendor.opening_balance });
    allInvoices.sort((a, b) => new Date(a.date) - new Date(b.date)).forEach(invoice => {
      const date = new Date(invoice.date);
      const purchase = Number(invoice.after_discount ?? invoice.amount ?? 0);
      const remaining = Number(invoice.remaining_amount ?? purchase);
      const paid = Math.max(0, purchase - remaining);
      balance += purchase - paid;
      if ((!from || date >= from) && (!to || date <= to)) rows.push({ ...invoice, invoice_no: invoice.invoice_no === "opening_balance" ? "Opening balance" : invoice.invoice_no, debit: purchase, credit: paid, balance });
    });
    return rows;
  }, [profileVendor, vendorInvoices, ledgerFrom, ledgerTo]);

  const vendorTotals = useMemo(() => {
    const businessInvoices = vendorInvoices.filter(invoice => invoice.invoice_no !== "opening_balance");
    const purchases = businessInvoices.reduce((sum, invoice) => sum + Number(invoice.after_discount ?? invoice.amount ?? 0), 0);
    const paid = businessInvoices.reduce((sum, invoice) => {
      const net = Number(invoice.after_discount ?? invoice.amount ?? 0);
      return sum + Math.max(0, net - Number(invoice.remaining_amount ?? net));
    }, 0);
    const opening = Number(profileVendor?.opening_balance || 0);
    const outstanding = vendorInvoices.length
      ? vendorInvoices.reduce((sum, invoice) => sum + Number(invoice.remaining_amount || 0), 0)
      : opening;
    const stockValue = vendorStock.reduce((sum, lot) => sum + (Number(lot.total_stock_arrived || 0) * Number(lot.purchase_price || 0)), 0);
    const stockUnits = vendorStock.reduce((sum, lot) => sum + Number(lot.total_stock_arrived || 0), 0);
    return { purchases, paid, outstanding, stockValue, stockUnits };
  }, [profileVendor, vendorInvoices, vendorStock]);

  const exportVendorLedger = () => {
    const rows = [["Date", "Invoice", "Status", "Purchase", "Paid", "Balance"], ...ledger.map(row => [row.date || "", row.invoice_no, row.status, row.debit, row.credit, row.balance])];
    const escape = value => `"${String(value ?? "").replaceAll('"', '""')}"`;
    const url = URL.createObjectURL(new Blob([rows.map(row => row.map(escape).join(",")).join("\n")], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `${profileVendor?.name || "vendor"}-ledger.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const printVendorLedger = () => {
    const popup = window.open("", "_blank", "width=1000,height=760");
    if (!popup) return;
    popup.document.write(`<html><head><title>${profileVendor?.name || "Vendor"} ledger</title><style>body{font-family:Arial;padding:28px;color:#17243b}table{width:100%;border-collapse:collapse}th,td{border:1px solid #ddd;padding:9px;text-align:left}th{background:#eef7f5}.num{text-align:right}</style></head><body><h1>${profileVendor?.name || "Vendor"} ledger</h1><p>${ledgerFrom || "Beginning"} to ${ledgerTo || "Today"}</p><table><thead><tr><th>Date</th><th>Invoice</th><th>Status</th><th>Purchase</th><th>Paid</th><th>Balance</th></tr></thead><tbody>${ledger.map(row => `<tr><td>${row.date ? new Date(row.date).toLocaleDateString() : "—"}</td><td>${row.invoice_no}</td><td>${row.status}</td><td class="num">${row.debit.toFixed(2)}</td><td class="num">${row.credit.toFixed(2)}</td><td class="num">${row.balance.toFixed(2)}</td></tr>`).join("")}</tbody></table></body></html>`);
    popup.document.close();
    popup.focus();
    popup.print();
  };

  const activeVendors = filteredVendors.length;
  const purchaseVendors = filteredVendors.filter((vendor) => vendor.type === "purchase").length;
  const expenseVendors = filteredVendors.filter((vendor) => vendor.type === "expense").length;
  const totalOpeningBalance = filteredVendors.reduce(
    (sum, vendor) => sum + Number(vendor.opening_balance || 0),
    0
  );

  return (
    <>
        <div className="responsive-box settings-workspace-panel vendor-workspace-panel">
          <ToastContainer />

          <div className="settings-section-header vendor-page-hero">
            <div>
              <span className="settings-section-kicker">Vendor workspace</span>
              <h2>Vendor Management</h2>
              <p>Create, classify and maintain purchase or expense vendors with department mapping and opening balances.</p>
            </div>
            <button
              type="button"
              className="settings-primary-action vendor-primary-action"
              onClick={handleShow}
            >
              <Plus size={18} />
              Add Vendor
            </button>
          </div>

          <div className="vendor-summary-grid">
            <div className="vendor-summary-card">
              <span className="vendor-summary-icon blue"><Users size={20} /></span>
              <div>
                <small>Active Vendors</small>
                <strong>{activeVendors}</strong>
              </div>
            </div>
            <div className="vendor-summary-card">
              <span className="vendor-summary-icon green"><Store size={20} /></span>
              <div>
                <small>Purchase Vendors</small>
                <strong>{purchaseVendors}</strong>
              </div>
            </div>
            <div className="vendor-summary-card">
              <span className="vendor-summary-icon amber"><Tags size={20} /></span>
              <div>
                <small>Expense Vendors</small>
                <strong>{expenseVendors}</strong>
              </div>
            </div>
            <div className="vendor-summary-card">
              <span className="vendor-summary-icon purple"><WalletCards size={20} /></span>
              <div>
                <small>Opening Balance</small>
                <strong>{totalOpeningBalance.toLocaleString()}</strong>
              </div>
            </div>
          </div>

          <section className="settings-detail-section vendor-table-panel">
            <div className="settings-subsection-header">
              <div>
                <h3>All Vendors</h3>
                <p>Search, edit or remove vendor records used across purchase, expense and reports.</p>
              </div>
            </div>
          <ReusableTable
            showFooter={true}
            fetchData={fetchVendorData}
            columns={[
              { header: "Vendor Name", field: "name" },
              { header: "Email", field: "email" },
              { header: "Vendor Type", field: "type" },
              { header: "Opening Balance", field: "opening_balance" },
              {
                header: "Department / Expense Type",
                field: "vendor_department_service_title",
              },
              {
                header: "Profile",
                field: "profile",
                render: (vendor) => <button type="button" className="vendor-profile-button" onClick={() => openVendorProfile(vendor)}><Eye size={15} /> View</button>,
              },
            ]}
            showAction={true}
            onEdit={handleEdit}
            onDelete={handleDelete}
            loading={loading}
            refreshKey={RefreshTrigger}
          />
          </section>
          <ReusableModal
            show={showModal}
            handleClose={handleClose}
            title={editVendorId ? "Edit Vendor" : "Add Vendor"}
            width="860px"
          >
            <form
              className="vendor-modal-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleSave();
              }}
            >
              <div className="vendor-modal-grid">
                  <div className="position-relative vendor-form-field">
                    <input
                      type="text"
                      id="vendorName"
                      className="form-control-setting"
                      placeholder=""
                      value={vendorName}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (/^[A-Za-z\s]*$/.test(val)) {
                          handleVendorNameChange(e);
                        }
                      }}
                    />
                    <label className="floating-label">Vendor Name</label>
                  </div>

                  <div className="position-relative vendor-form-field">
                    <select
                      id="vendorType"
                      className="form-control-setting vendor-form-select"
                      value={vendorType}
                      onChange={(e) => {
                        setVendorType(e.target.value);
                        setVendorDepartments([]);
                        setOpeningBalance("");
                      }}
                    >
                      <option value="">Vendor Type</option>
                      <option value="purchase">Purchase</option>
                      <option value="expense">Expense</option>
                    </select>
                  </div>

                {(vendorType === "purchase" || vendorType === "expense") && (
                  <div className="vendor-form-field vendor-form-field-wide">
                    <label className="vendor-field-label">
                      {vendorType === "purchase" ? "Vendor Departments" : "Expense Types"}
                    </label>
                      <ChipList
                        items={vendorDepartments}
                        onRemove={handleChipRemove}
                        displayField="title"
                        keyField="id"
                        style={{ marginBottom: "10px" }}
                      />

                      <PaginatedSearchSelect
                        api="vendorDepartmentServiceData"
                        GetPaginatedData={GetPaginatedData}
                        getOptionLabel={(opt) => opt.all_department_detail?.title || opt.all_expense_type_detail?.title}
                        onSelect={handleDepartmentSelect}
                        placeholder={vendorType === "purchase" ? "Select Vendor Department" : "Select Expense Type"}
                        filterFn={(item) => {
                          const itemType = item.all_department_detail ? "department" : "expense";
                          const expectedType = vendorType === "purchase" ? "department" : "expense";
                          return itemType === expectedType && !vendorDepartments.some(d => d.id === item.id);
                        }}
                      />

                    {vendorType === "purchase" && (
                      <div className="position-relative vendor-form-field vendor-opening-balance-field">
                        <input
                          type="number"
                          id="openingBalance"
                          className="form-control-setting"
                          placeholder=""
                          value={openingBalance}
                          onChange={(e) => setOpeningBalance(e.target.value)}
                        />
                        <label className="floating-label">
                          Opening Balance
                        </label>
                      </div>
                    )}
                  </div>
                )}
                <div className="position-relative vendor-form-field">
                  <Mail className="vendor-input-icon" size={18} />
                  <input
                    type="email"
                    id="vendorEmail"
                    className="form-control-setting vendor-email-input"
                    placeholder=""
                    value={vendorEmail}
                    onChange={handleEmailChange}
                    style={{
                      borderBottom: emailError ? "2px solid #dc3545" : "2px solid #ced4da"
                    }}
                  />
                  <label className="floating-label">Email</label>
                  {emailError && (
                    <div style={{ color: "#dc3545", fontSize: "12px", marginTop: "4px" }}>
                      {emailError}
                    </div>
                  )}
                </div>
              </div>
                <div className="vendor-modal-actions">
                  {!editVendorId && (
                    <button
                      type="button"
                      onClick={handleClearVendor}
                      className="vendor-secondary-action"
                    >
                      Clear
                    </button>
                  )}

                  <button
                    type="submit"
                    className="vendor-save-action"
                  >
                    {editVendorId ? "Update" : "Save"}
                  </button>
                </div>
            </form>
          </ReusableModal>
          <ReusableModal show={Boolean(profileVendor)} handleClose={() => setProfileVendor(null)} title="Vendor Profile" width="1100px">
            {profileVendor && <div className="vendor-profile">
              <div className="vendor-profile-heading"><div className="vendor-avatar"><Store size={24} /></div><div><h2>{profileVendor.name}</h2><p>{profileVendor.email || "No email provided"} · {profileVendor.type} vendor</p></div></div>
              <div className="vendor-financial-grid"><article><small>{profileVendor.type === "expense" ? "Total expenses" : "Invoiced purchases"}</small><strong>₹{vendorTotals.purchases.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong></article><article><small>Stock received value</small><strong>₹{vendorTotals.stockValue.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong><em>{vendorTotals.stockUnits.toLocaleString("en-IN")} units</em></article><article><small>Total paid</small><strong>₹{vendorTotals.paid.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong></article><article><small>Opening balance</small><strong>₹{Number(profileVendor.opening_balance || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong></article><article className="vendor-due-card"><small>Outstanding</small><strong>₹{vendorTotals.outstanding.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong></article></div>
              <section className="vendor-stock-activity"><div className="vendor-stock-header"><div><h3><Boxes size={18} /> Stock Receipts</h3><p>Inventory lots added against this vendor. These remain separate from the payable ledger until invoiced.</p></div><span>{vendorStock.length} lots</span></div><div className="vendor-stock-table"><table><thead><tr><th>Date</th><th>Product</th><th>Batch</th><th>Received</th><th>Current</th><th>Purchase price</th><th>Stock value</th></tr></thead><tbody>{vendorStock.map(lot => <tr key={lot.id}><td>{lot.date ? new Date(lot.date).toLocaleString() : "—"}</td><td><strong>{lot.product_title || lot.product}</strong></td><td>{lot.batch || "—"}</td><td>{Number(lot.total_stock_arrived || 0).toLocaleString("en-IN")}</td><td>{Number(lot.current_stock || 0).toLocaleString("en-IN")}</td><td>₹{Number(lot.purchase_price || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td><td><strong>₹{(Number(lot.total_stock_arrived || 0) * Number(lot.purchase_price || 0)).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong></td></tr>)}</tbody></table>{profileLoading && <p>Loading stock receipts…</p>}{!profileLoading && !vendorStock.length && <p>No stock receipts found for this vendor.</p>}</div></section>
              <section className="vendor-ledger"><div className="vendor-ledger-header"><div><h3><FileText size={18} /> Vendor Ledger</h3><p>Purchase invoices, payments and running balance.</p></div><div className="vendor-ledger-actions"><button type="button" onClick={exportVendorLedger}><Download size={15} /> CSV</button><button type="button" onClick={printVendorLedger}><Printer size={15} /> Print</button></div></div>
                <div className="vendor-ledger-filters"><label>From<input type="date" value={ledgerFrom} onChange={event => setLedgerFrom(event.target.value)} /></label><label>To<input type="date" value={ledgerTo} onChange={event => setLedgerTo(event.target.value)} /></label></div>
                <div className="vendor-ledger-table"><table><thead><tr><th>Date</th><th>Invoice</th><th>Status</th><th>Purchase</th><th>Paid</th><th>Balance</th></tr></thead><tbody>{ledger.map(row => <tr key={row.id}><td>{row.date ? new Date(row.date).toLocaleDateString() : "—"}</td><td><strong>{row.invoice_no}</strong></td><td><span className={`vendor-ledger-status ${row.status}`}>{String(row.status || "—").replaceAll("_", " ")}</span></td><td>₹{row.debit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td><td>₹{row.credit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td><td><strong>₹{row.balance.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong></td></tr>)}</tbody></table>{profileLoading && <p>Loading vendor purchases…</p>}{!profileLoading && !ledger.length && <p>No purchase invoices found for this vendor.</p>}</div>
              </section>
            </div>}
          </ReusableModal>
        </div>
    </>
  );
}

export default ManageVendor;
